import type { CameraView } from './cameraFly';

/**
 * Saved "home view" persistence (#149).
 *
 * "Set as my home view" stores the operator's current camera vantage; the home
 * button / `H` keybind flies back to it, and a fresh load with no deep-link
 * frames it (falling back to the all-Canada default — #138 — when unset).
 *
 * Stored in localStorage, NOT the server. The existing operator-preferences
 * store (`operator_layer_preferences`) is boolean-keyed by a fixed LayerKey
 * enum, so it can't hold a {lat,lon,heightM} vantage; a server-synced home view
 * would need a schema migration (out of scope for this no-migration session).
 * This keeps the same load-on-startup / save-on-change ergonomics as the layer
 * prefs and is a clean seam to lift onto the server later (cross-device sync).
 */

const KEY = 'tsr.homeView';

function isView(v: unknown): v is CameraView {
  if (!v || typeof v !== 'object') return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c.lat === 'number' &&
    typeof c.lon === 'number' &&
    typeof c.heightM === 'number' &&
    Number.isFinite(c.lat) &&
    Number.isFinite(c.lon) &&
    Number.isFinite(c.heightM)
  );
}

/** The operator's saved home view, or null when none is set / storage is empty. */
export function loadHomeView(): CameraView | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isView(parsed) ? { lat: parsed.lat, lon: parsed.lon, heightM: parsed.heightM } : null;
  } catch {
    return null;
  }
}

/** Persist the operator's home view (overwrites any prior one). */
export function saveHomeView(view: CameraView): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(view));
  } catch {
    /* ignore */
  }
}

/** Clear the saved home view (reverts cold-start framing to the Canada default). */
export function clearHomeView(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
